import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Gastrointestinal subsection adapter
 * Handles the most complex GI/GU assessment with feeding tubes, catheters, nutrition (19 fields)
 * This is the most complex subsection with many interdependent fields
 */
export class GastrointestinalAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // GI assessment
    const giAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT
    )

    const bmDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE
    )?.at(0)

    // GU assessment
    const guAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT
    )

    // Catheter assessment
    const catheter = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER
    )?.at(0)

    const catheterType = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE
    )

    // Feeding tube assessment - complex with many related fields
    const feedingTube = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE
    )?.at(0)

    const feedingTubeAmount = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT
    )?.at(0)

    const feedingTotalNumber = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER
    )?.at(0)

    const feedingTubeDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION
    )?.at(0)

    const feedingTubeType = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE
    )?.at(0)

    const feedingTubeSite = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE
    )

    const feedingTubeSize = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE
    )?.at(0)

    const feedingTubeNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES
    )?.at(0)

    // Nutrition assessment
    const nutritionWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL
    )?.at(0)

    const nutritionalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS
    )

    const dietRequirements = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS
    )?.at(0)

    const nutritionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES
    )?.at(0)

    const nutritionalScreening = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING
    )

    const historyNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.GASTROINTESTINAL_GI_ASSESSMENT]: giAssessment,
      [RocFormField.GASTROINTESTINAL_BM_DATE]: bmDate,
      [RocFormField.GASTROINTESTINAL_GU_ASSESSMENT]: guAssessment,
      [RocFormField.GASTROINTESTINAL_CATHETER]: catheter,
      [RocFormField.GASTROINTESTINAL_CATHETER_TYPE]: catheterType,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE]: feedingTube,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT]: feedingTubeAmount as number,
      [RocFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER]: feedingTotalNumber as number,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION]: feedingTubeDuration,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE]: feedingTubeType,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE]: feedingTubeSite,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE]: feedingTubeSize,
      [RocFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES]: feedingTubeNotes,
      [RocFormField.GASTROINTESTINAL_NUTRITION_WNL]: nutritionWnl,
      [RocFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS]: nutritionalFactors,
      [RocFormField.GASTROINTESTINAL_DIET_REQUIREMENTS]: dietRequirements,
      [RocFormField.GASTROINTESTINAL_NUTRITION_NOTES]: nutritionNotes,
      [RocFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING]: nutritionalScreening,
      [RocFormField.GASTROINTESTINAL_HISTORY_NOTES]: historyNotes,
      [RocFormField.GASTROINTESTINAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_GI_ASSESSMENT)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_GI_ASSESSMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_BM_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_BM_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_GU_ASSESSMENT)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_GU_ASSESSMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_CATHETER)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_CATHETER)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_CATHETER_TYPE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_CATHETER_TYPE)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_NUTRITION_WNL)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_NUTRITION_WNL)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_NUTRITION_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_NUTRITION_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_HISTORY_NOTES)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_HISTORY_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.GASTROINTESTINAL_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.GASTROINTESTINAL_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
